// src/stores/consumerSiteConfigStore.js
//
// Consumer site content configuration.
// Reads from publicConfig/consumerSiteConfig via onSnapshot (authenticated) or
// HTTP Cloud Function fallback (unauthenticated visitors blocked by App Check).

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'

const DEFAULTS = {
  products: [
    {
      id: '',
      type: 'Cash',
      title: 'Cash',
      description:
        'This option works great if you have, well, the cash to do it. You put up the money up front to pay for the install and purchase the equipment. In doing that you save money at the meter and you recoup money from the government tax credits. Win, win.',
      sortOrder: 1,
      enabled: true,
    },
    {
      id: '',
      type: 'Lease',
      title: 'PPA',
      description:
        "If you don't have the cash on hand, don't worry. We set up a PPA (Power Purchase Agreement) option to get solar onto as many roofs as possible. We use the tax credits to pay for install and equipment. And you get to recoup a huge savings on your electric bill immediately. A huge win for you and your wallet.",
      sortOrder: 2,
      enabled: true,
    },
    {
      id: '',
      type: 'Cash',
      title: 'Loan',
      description:
        "If you're not a huge fan of the other two options, we can offer you a traditional loan to pay off the initial cash investment. Once that's paid off, the equipment is yours free and clear. You'll start making your money from the meter and the tax credits. Yet another way to win.",
      sortOrder: 3,
      enabled: true,
    },
    {
      id: '',
      type: 'ILSFA',
      title: 'Illinois Solar for All',
      description:
        "Illinois Solar for All makes solar accessible for income-eligible households. No upfront costs, and the program's renewable energy credits help keep your rate affordable. Your panels create energy from the sun, and your savings start immediately. If you qualify, GSE handles the application and gets you saving from day one.",
      sortOrder: 4,
      enabled: true,
    },
  ],
  whyNowText:
    "Chances are you've been thinking about making the switch for a while. Energy rates are going nowhere but up. With solar credits from net metering and our affordable Power Purchase Agreement (PPA), you'll pay less every month. No big upfront costs \u2014 just solar energy at a lower rate. Lock in savings now and watch your utility bill shrink.",
  faqs: [
    {
      question: 'How do I qualify for the Federal Solar Tax Credit?',
      hint: "(It's easier than you think)",
      answer:
        'The federal solar tax credit (ITC) allows you to deduct a percentage of the cost of installing a solar energy system from your federal taxes. Most homeowners who own their system qualify, though not all customers are eligible. We help you understand what applies to your situation and maximize your benefit.',
      productType: null,
    },
    {
      question: 'Can I really go solar with no money down?',
      hint: "(Don't believe us?)",
      answer:
        'Yes! With our PPA (Power Purchase Agreement) option, we cover the upfront costs of installation and equipment. You simply pay a lower rate for the power your panels produce. No big investment required \u2014 just immediate savings on your electric bill. The savings are real, and they start on day one.',
      productType: 'Lease',
    },
    {
      question: 'How long does installation take?',
      hint: '(It depends)',
      answer:
        'Most residential installations are completed in 1-3 days. The full process \u2014 from signing to flipping the switch \u2014 typically takes 4-8 weeks, depending on permits, utility approvals, and your local jurisdiction. We keep you updated every step of the way.',
      productType: null,
    },
    {
      question: 'Does GSE help with permits and paperwork?',
      hint: '(Yep)',
      answer:
        "Absolutely. We handle all the permits, utility interconnection paperwork, and HOA approvals. You don't have to chase down a single form \u2014 our team takes care of everything so you can focus on enjoying your new solar system.",
      productType: null,
    },
    {
      question: 'What happens if something breaks?',
      hint: '(Just give us a call)',
      answer:
        "Your solar panels come with manufacturer warranties (typically 25 years), and we stand behind our installation work. If anything goes wrong, just call us \u2014 we'll diagnose the issue and get it fixed. We're here for the long haul.",
      productType: null,
    },
    {
      question: 'Does solar increase my home value?',
      hint: '(It sure does)',
      answer:
        "Studies consistently show that solar panels increase home values. Homes with solar sell faster and for more money than comparable homes without. It's an investment that pays you back now and when you sell.",
      productType: null,
    },
    {
      question: 'Will solar work during winter or cloudy days?',
      hint: '(Yes, but not quite as well)',
      answer:
        'Solar panels still generate electricity on cloudy days and during winter \u2014 just at a reduced rate. Your system is designed to account for seasonal variations, and net metering credits from sunny months help offset darker periods.',
      productType: null,
    },
    {
      question: 'What makes you different from everyone else?',
      hint: "(We're there for you)",
      answer:
        "We price our systems to get as many people on solar as possible \u2014 not to maximize our profit per install. We're transparent, responsive, and we actually call you back. Our mission is to make solar accessible to everyone, everywhere.",
      productType: null,
    },
    {
      question: "What's the deal with leasing vs owning?",
      hint: "(We'll get into that)",
      answer:
        "Owning (via cash or loan) gives you the tax credits and full long-term savings. Leasing or a PPA means lower upfront costs with immediate bill savings, but the company retains ownership. We'll walk you through your options so you can pick what's best for you.",
      productType: 'Lease',
    },
  ],
  ilsfaDisclosureText: '',
  // Low-production-rate warning (docs/features/low_production_rate_warning_FEATURE.md):
  // friendly consumer-facing callout when a site's analysis rate scores below the state's
  // configured design floor. Copy is operator-editable (Settings → Consumer Site); this
  // default only seeds the editor and serves until the doc carries the field.
  lowProductionWarning: {
    enabled: false,
    message:
      "Your site's estimated production rate is lower than we would like for solar. " +
      'You can still continue with your design, but your site may not work well for solar ' +
      'without tree removal or other modifications to the site.',
  },
}

export const useConsumerSiteConfigStore = defineStore('consumerSiteConfig', () => {
  const config = ref({ ...DEFAULTS })
  const loaded = ref(false)
  let _unsubscribe = null

  // ── Public Getters ────────────────────────────────────────────────────────

  const activeProducts = computed(() =>
    config.value.products
      .filter(p => p.enabled !== false)
      .sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99))
  )

  const enabledProductTypes = computed(() => {
    const types = new Set()
    for (const p of activeProducts.value) {
      if (p.type) types.add(p.type)
    }
    return [...types]
  })

  const productCount = computed(() => activeProducts.value.length)

  const showIlsfaDisclosure = computed(() => enabledProductTypes.value.includes('ILSFA'))

  const visibleFaqs = computed(() =>
    (config.value.faqs || []).filter(faq => {
      if (!faq.productType) return true
      return enabledProductTypes.value.includes(faq.productType)
    })
  )

  // ── Subscription (no auth, public doc) ────────────────────────────────────

  function subscribe() {
    if (_unsubscribe) return

    let resolved = false

    // Race: try both onSnapshot AND HTTP fetch simultaneously
    // Whichever succeeds first wins
    const docRef = doc(db, 'publicConfig', 'consumerSiteConfig')

    // Start HTTP fetch immediately (no waiting)
    import('@/utils/publicConfigLoader')
      .then(({ fetchPublicConfig }) => {
        fetchPublicConfig(['consumerSiteConfig'])
          .then(data => {
            if (!resolved && data?.consumerSiteConfig) {
              resolved = true
              applyData(data.consumerSiteConfig)
              loaded.value = true
            }
          })
          .catch(() => {})
      })
      .catch(() => {})

    // Also try onSnapshot (works for authenticated users)
    _unsubscribe = onSnapshot(
      docRef,
      snap => {
        if (snap.exists()) {
          resolved = true
          applyData(snap.data())
        }
        loaded.value = true
      },
      () => {
        // Firestore error — HTTP fetch above will handle it
      }
    )
  }

  function applyData(data) {
    config.value = {
      products: data.products || DEFAULTS.products,
      whyNowText: data.whyNowText || DEFAULTS.whyNowText,
      faqs: data.faqs || DEFAULTS.faqs,
      ilsfaDisclosureText: data.ilsfaDisclosureText || DEFAULTS.ilsfaDisclosureText,
      lowProductionWarning: {
        ...DEFAULTS.lowProductionWarning,
        ...(data.lowProductionWarning || {}),
      },
    }
  }

  function unsubscribe() {
    if (_unsubscribe) {
      _unsubscribe()
      _unsubscribe = null
    }
    loaded.value = false
  }

  // ── Admin Save ────────────────────────────────────────────────────────────

  async function saveConfig(newConfig, userId) {
    const docRef = doc(db, 'publicConfig', 'consumerSiteConfig')
    await setDoc(docRef, {
      ...newConfig,
      _metadata: {
        updatedBy: userId || 'unknown',
        updatedAt: serverTimestamp(),
      },
    })
  }

  return {
    config,
    loaded,
    activeProducts,
    enabledProductTypes,
    productCount,
    showIlsfaDisclosure,
    visibleFaqs,
    subscribe,
    unsubscribe,
    saveConfig,
  }
})
